/**
 * PF Revamp — primary "new order" action.
 *
 * Encapsulates the decision tree behind the primary order CTA so every surface
 * that needs it shares one implementation (DRY):
 *   - NewOrderButton (desktop TopBar)
 *   - TopBar mobile order CTA
 *
 * Decision tree:
 *   - revamp OFF      → navigate to the legacy purchase flow (+ optional side effect)
 *   - property-first  → open the property-selection drawer over the current page; it also lists saved orders
 *   - otherwise       → navigate to the resolved entry path
 *
 * Callers own the drawer state because that UI is TopBar-local.
 */
import { useCallback } from 'react'

import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { Path } from 'constants/router'
import { useNavigate } from 'react-router-dom'
import { usePfEntryNavigation } from './usePfEntryNavigation'

interface Options {
  /** Called when the user should see the property-selection drawer (overlay over the current page). */
  onOpenPropertyDrawer: () => void
  /** Optional side effect fired right before navigating in legacy (revamp OFF) mode — e.g. analytics. */
  onLegacyNavigate?: () => void
}

export interface PfEntryPrimaryAction {
  /** Runs the resolved primary order action for the current user. */
  handlePrimaryAction: () => void
}

export function usePfEntryPrimaryAction({ onOpenPropertyDrawer, onLegacyNavigate }: Options): PfEntryPrimaryAction {
  const navigate = useNavigate()

  const allowPfRevamp = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP)
  const { entryPath } = usePfEntryNavigation()

  const handlePrimaryAction = useCallback(() => {
    if (!allowPfRevamp) {
      onLegacyNavigate?.()
      navigate(Path.PURCHASE_FLOW)
      return
    }
    // Property-first variant resolves to /select-property — but routing to that
    // page strands the user on a blank canvas if they X out (dogfood-2 finding 2).
    // Open the drawer over the current page instead.
    if (entryPath === Path.PURCHASE_FLOW_REVAMP_SELECT_PROPERTY) {
      onOpenPropertyDrawer()
      return
    }
    navigate(entryPath)
  }, [allowPfRevamp, entryPath, navigate, onOpenPropertyDrawer, onLegacyNavigate])

  return { handlePrimaryAction }
}
